import { useEffect, useState } from 'react';
import { TOAST_STACK_GAP_PX, TOAST_TOP_OFFSET_PX } from './Notification.utils';

export const TOAST_STACK_TRANSITION_MS = 400;

const VISIBLE_TOAST_SELECTOR =
  '[data-sonner-toast][data-mounted="true"]:not([data-visible="false"])';

const EXPANDED_TOAST_SELECTOR =
  '[data-sonner-toast][data-mounted="true"][data-expanded="true"]';

const MUTATION_ATTRIBUTES = [
  'data-expanded',
  'data-mounted',
  'data-visible',
  'data-removed',
] as const;

const REFRESH_EVENTS = [
  'mouseenter',
  'mouseleave',
  'focusin',
  'focusout',
  'pointerup',
] as const;

const getVisibleToasts = (toasterRoot: HTMLElement) =>
  Array.from(toasterRoot.querySelectorAll<HTMLElement>(VISIBLE_TOAST_SELECTOR));

const parseCssPixelValue = (value: string) => {
  if (!value || value === 'auto') return null;

  const parsed = Number.parseFloat(value);

  return Number.isFinite(parsed) ? parsed : null;
};

const getToastHeight = (toastEl: HTMLElement) => {
  const initialHeight = parseCssPixelValue(
    toastEl.style.getPropertyValue('--initial-height')
  );

  return initialHeight ?? toastEl.offsetHeight;
};

const getFrontToastHeight = (
  toasterRoot: HTMLElement,
  toasts: HTMLElement[]
) => {
  const toasterList = toasterRoot.querySelector<HTMLElement>(
    '[data-sonner-toaster]'
  );
  const frontToastHeight = toasterList
    ? parseCssPixelValue(
        getComputedStyle(toasterList).getPropertyValue('--front-toast-height')
      )
    : null;

  if (frontToastHeight && frontToastHeight > 0) return frontToastHeight;

  const [frontToast] = toasts;

  return frontToast ? getToastHeight(frontToast) : 0;
};

const isErrorStackExpanded = (toasterRoot: HTMLElement) =>
  Boolean(toasterRoot.querySelector(EXPANDED_TOAST_SELECTOR));

const measureErrorStackHeight = (toasterRoot: HTMLElement) => {
  const toasts = getVisibleToasts(toasterRoot);

  if (!toasts.length) return 0;

  const gapTotal = TOAST_STACK_GAP_PX * (toasts.length - 1);

  if (isErrorStackExpanded(toasterRoot)) {
    const expandedHeight = toasts.reduce(
      (sum, toastEl) => sum + getToastHeight(toastEl),
      0
    );

    return expandedHeight + gapTotal;
  }

  return getFrontToastHeight(toasterRoot, toasts) + gapTotal;
};

const getNonErrorTopOffsetPx = (errorStackHeight: number) => {
  if (errorStackHeight <= 0) return TOAST_TOP_OFFSET_PX;

  return TOAST_TOP_OFFSET_PX + errorStackHeight + TOAST_STACK_GAP_PX;
};

const shouldRefresh = (mutations: MutationRecord[]) =>
  mutations.some(mutation => {
    if (mutation.type === 'childList') return true;

    return (
      mutation.attributeName !== null &&
      (MUTATION_ATTRIBUTES as readonly string[]).includes(
        mutation.attributeName
      )
    );
  });

const observeToaster = (
  toasterRoot: HTMLElement,
  onChange: () => void
): (() => void) => {
  let frameId = 0;

  const scheduleUpdate = () => {
    cancelAnimationFrame(frameId);
    frameId = requestAnimationFrame(onChange);
  };

  scheduleUpdate();

  REFRESH_EVENTS.forEach(eventName => {
    toasterRoot.addEventListener(eventName, scheduleUpdate);
  });

  const mutationObserver = new MutationObserver(mutations => {
    if (shouldRefresh(mutations)) scheduleUpdate();
  });

  mutationObserver.observe(toasterRoot, {
    childList: true,
    subtree: true,
    attributes: true,
    attributeFilter: [...MUTATION_ATTRIBUTES],
  });

  return () => {
    cancelAnimationFrame(frameId);
    mutationObserver.disconnect();
    REFRESH_EVENTS.forEach(eventName => {
      toasterRoot.removeEventListener(eventName, scheduleUpdate);
    });
  };
};

export const useNonErrorToasterTopOffset = (
  errorToasterEl: HTMLElement | null
) => {
  const [topOffsetPx, setTopOffsetPx] = useState(TOAST_TOP_OFFSET_PX);

  useEffect(() => {
    if (!errorToasterEl) return;

    return observeToaster(errorToasterEl, () => {
      setTopOffsetPx(
        getNonErrorTopOffsetPx(measureErrorStackHeight(errorToasterEl))
      );
    });
  }, [errorToasterEl]);

  return `${topOffsetPx}px`;
};
